import { API_BASE_URL, API_ENDPOINTS } from '@common/constants/apiEndpoint';
import BaseComponent from '@nocode/types/base.type';
import { get, isEmpty } from 'lodash';
import queryString from 'query-string';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Platform } from 'react-native';
import { getValueBinding, isImageFile } from '../shared';
import EventSource from 'react-native-sse';
import {
  DEFAULT_OPEN_AI_AI_URL,
  GptConversationResponseModeEnum,
  GptConversationServiceTypeEnum,
} from '@common/constants/gpt-conversation.constant';
import { TextAlign } from '@nocode/types/common.type';
import {
  GptConversationActionType,
  IMessage,
  MessageFile,
} from '@common/hooks/useGptConversation';
import { uniqBy } from 'lodash';
import { FileType, UploadFileActionType } from '@common/hooks/useUploadFile';

const INIT_MESSAGE_ID = 'init';
type GptConfiguration = {
  apiUrl?: string;
  openAiModel?: string;
  serviceType: GptConversationServiceTypeEnum;
  apiKey: string;
  assistantId?: string;
  threadId?: string;
  responseId?: string;
  responseMode?: keyof typeof GptConversationResponseModeEnum;
};
type TextStyle = {
  fontSize: number;
  color: string;
  fontFamily: string;
  backgroundColor: string;
  lineHeight: number;
};
export type IGptConversation = BaseComponent & {
  attributes: {
    backgroundColor: string;
    fontFamily: string;
    fontSize: number;
    fontWeight: string;
    lineHeight: number;
    opacity: number;
    textAlignment: TextAlign;
    borderRadius: number;
    sendButton: {
      icon: string;
      iconEnableOutline: boolean;
    } & TextStyle;
    // TODO deprecate
    advanceSetting: {
      aiName: string | any;
      username: string | any;
      aiColor: string;
      userColor: string;
    };
    chatbot: {
      color: string;
      backgroundColor: string;
      icon: string;
      iconColor: string;
      iconBackgroundColor: string;
    };
    user: {
      color: string;
      backgroundColor: string;
      icon: string;
      iconColor: string;
      iconBackgroundColor: string;
    };
  };
  data: Record<string, any>;
  appId: string;
} & {
  gptActions?: GptConversationActionType;
  fileActions?: UploadFileActionType;
};
export type GptConversationBindingValue = GptConfiguration & {
  placeholder: string;
  initPrompt: string;
  supportUploadFile: boolean;
  webSearchTool: boolean;
  'advanceSetting.aiName': string;
  'advanceSetting.username': string;
  'sendButton.text': string;
  initMessage: string;
};
type MessageQuery = { role: 'user' | 'assistant' | 'system'; content: string };
type FileResponse = { filename: string; fileId: string; containerId?: string };
type CreateChatMessageCallBacks = {
  onResource?: (_: { url: string }) => void;
  onAttachment?: (_: {
    senderType: 'ai' | 'user';
    answer?: string;
    fileId: string;
    containerId?: string;
  }) => void;
  onAnswer?: (formattedAnswer: string) => void;
  onDone?: (data: {
    answer: string;
    error: string;
    files: FileResponse[];
  }) => void;
};
type CreateChatMessageRequest = {
  query?: string;
  messages?: MessageQuery[];
  metadata?: {
    fileUrl?: string;
  };
  appId: string;
  username: string;
  conversationId?: string | null;
  threadId?: string | null;
  responseId?: string | null;
  webSearchTool: boolean;
  initPrompt?: string;
  timezone: string;
};

type BaseResponseMessage = {
  senderType?: 'ai' | 'user';
  answer?: string;
  isEnd?: boolean;
  threadId?: string;
  responseId?: string;
  error?: string;
  fileId?: string;
  containerId?: string;
  conversationId?: string | null;
  resources?: { url: string }[];
};
type ResponseMessageText = BaseResponseMessage & {};
type ResponseMessageFile = BaseResponseMessage & {
  fileId: string;
  containerId?: string;
};
type ResponseMessage = ResponseMessageText | ResponseMessageFile;

export const useGptConversation = (props: IGptConversation) => {
  const [historyInitialized, setHistoryInitialized] = useState(false);
  const [getHistoryLoading, setGetHistoryLoading] = useState(false);
  const [fileSelected, setFileSelected] = useState<FileType | null>(null);
  const { data, appId, changeInput, setDefaultValue } = props;
  const actions = props.gptActions;
  const bindingValue = getValueBinding(
    props.id,
    data,
    props
  ) as GptConversationBindingValue;

  const username =
    get(bindingValue, 'advanceSetting.username')?.toString() ||
    get(bindingValue, 'user.name') ||
    'あなた';
  const aiName =
    get(bindingValue, 'advanceSetting.aiName')?.toString() ||
    get(bindingValue, 'chatbot.name') ||
    'AIチャットボット名';
  const submitButtonText =
    get(bindingValue, 'sendButton.text')?.toString() || '質問する';
  const supportUploadFile = !!get(bindingValue, 'supportUploadFile', false);
  const webSearchTool = !!get(bindingValue, 'webSearchTool', false);
  const placeholderText = get(bindingValue, 'placeholder')?.toString();
  const getSenderName = (senderType: IMessage['senderType']) => {
    switch (senderType) {
      case 'user':
        return `${username}`;
      case 'ai':
      case 'error':
        return `${aiName}`;
    }
  };
  const initPrompt = get(bindingValue, 'initPrompt', '')?.toString();
  const initMessage = get(bindingValue, 'initMessage', '')?.toString();
  const serviceType = get(
    bindingValue,
    'serviceType',
    GptConversationServiceTypeEnum.openAi
  ) as GptConversationServiceTypeEnum;
  const initThreadId = get(bindingValue, 'threadId', null);
  const initResponseId = get(bindingValue, 'responseId', null);
  const assistantId = get(bindingValue, 'assistantId', '');
  const apiKey = get(bindingValue, 'apiKey', '');
  const responseMode = get(
    bindingValue,
    'responseMode',
    GptConversationResponseModeEnum.streaming
  ) as keyof typeof GptConversationResponseModeEnum;
  const gptConfig: GptConfiguration = {
    apiKey,
    apiUrl:
      serviceType === GptConversationServiceTypeEnum.openAi
        ? DEFAULT_OPEN_AI_AI_URL
        : get(bindingValue, 'apiUrl', DEFAULT_OPEN_AI_AI_URL),
    openAiModel: get(bindingValue, 'openAiModel'),
    serviceType,
    assistantId,
    responseMode,
  };
  const search = !isEmpty(window)
    ? queryString.parse(window?.location?.search)
    : {};
  const target = search?.target;
  const isCanvas = Platform.OS === 'web' && !target;

  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [aiRespondingText, setAiRespondingText] = useState<string | null>(null);
  const [aiResponding, setAiResponding] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(initThreadId);
  const [responseId, setResponseId] = useState<string | null>(initResponseId);
  const [initializing, setInitializing] = useState(true);
  const [messagesQuery, setMessagesQuery] = useState<MessageQuery[]>([]);

  const messageRef = useRef<any>();

  const scrollToBottom = () => {
    messageRef?.current?.scrollToEnd({
      animated: true,
    });
  };

  const addMessage = (
    message: Pick<
      IMessage,
      'senderType' | 'message' | 'messageType' | 'attachments'
    >
  ) => {
    setMessages((old) => [
      ...old,
      {
        id: Date.now().toString(),
        createdAt: Math.round(Date.now() / 1000),
        ...message,
      } as IMessage,
    ]);
  };
  const addInitMessage = (message: string) => {
    setMessages([
      {
        message,
        id: INIT_MESSAGE_ID,
        messageType: 'text',
        senderType: 'ai',
        createdAt: 0,
      },
    ]);
  };

  const addMessageQuery = (data: MessageQuery) => {
    if (gptConfig?.serviceType === GptConversationServiceTypeEnum.openAi) {
      messagesQuery.push(data);
      setMessagesQuery(messagesQuery);
    }
    return messagesQuery;
  };
  const handleChunkData = (
    chunk: string,
    callbacks?: CreateChatMessageCallBacks
  ) => {
    let aiRespondingTempt = '';
    let errorRespondingTempt = '';
    try {
      const jsonData = JSON.parse(chunk) as ResponseMessage;
      if (jsonData.answer && !jsonData.fileId) {
        const formattedAnswer = jsonData.answer.replace(/<br>/g, '\n');
        callbacks?.onAnswer?.(formattedAnswer);
        aiRespondingTempt = formattedAnswer;
      }
      if (jsonData.fileId) {
        callbacks?.onAttachment?.({
          fileId: jsonData.fileId,
          containerId: jsonData.containerId,
          answer: jsonData.answer || jsonData.fileId,
          senderType: jsonData.senderType || 'ai',
        });
      }
      if (jsonData.error) {
        const formattedError = jsonData.error.replace(/<br>/g, '\n');
        errorRespondingTempt = formattedError;
      }
      if (jsonData.conversationId) {
        setConversationId(jsonData.conversationId);
      }
      if (jsonData.resources && jsonData.resources.length > 0) {
        jsonData.resources.forEach((resource: { url: string }) => {
          callbacks?.onResource?.({ url: resource.url || '' });
        });
      }
      return { aiRespondingTempt, errorRespondingTempt, jsonData };
    } catch (e) {
      console.error('JSON の解析エラー:', e);
    }
    return null;
  };

  const setDefaultElementValue = (value: string) => {
    changeInput?.(value);
    setDefaultValue?.(value);
  };

  const handleAiResponseMobile = async (
    body: CreateChatMessageRequest,
    callbacks?: CreateChatMessageCallBacks
  ) => {
    let aiRespondingTempt = '';
    let errorRespondingTempt = '';
    const files: FileResponse[] = [];
    setAiResponding(true);
    const updatedGptConfig = { ...gptConfig };
    if (
      gptConfig.serviceType === GptConversationServiceTypeEnum.openAiAssistant
    ) {
      delete updatedGptConfig.apiUrl;
      delete updatedGptConfig.openAiModel;
    }

    if (
      gptConfig.serviceType ===
        GptConversationServiceTypeEnum.openAiAssistant &&
      body.threadId
    ) {
      await actions?.checkThreadRunning({
        apiKeyId: gptConfig.apiKey,
        threadId: body.threadId,
      });
    }

    const es = new EventSource(
      `${API_BASE_URL}/${API_ENDPOINTS.GPT_CONVERSATION_CHAT_MESSAGE}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        debug: true,
        body: JSON.stringify({
          ...body,
          gptConfig,
        }),
        timeoutBeforeConnection: 100,
        pollingInterval: 0, // Time (ms) between re-connections. If set to 0, re-connections will be disabled. Default: 5000
      }
    );
    const onDone = () => {
      callbacks?.onDone?.({
        answer: aiRespondingTempt,
        error: errorRespondingTempt,
        files,
      });
      setAiResponding(false);
      es.removeAllEventListeners();
      es.close();
    };
    es.addEventListener('open', (event) => {
      // console.log('===open event', event);
    });

    es.addEventListener('message', (event) => {
      const data = event.data;
      if (!data) {
        return;
      }
      const chunkResponse = handleChunkData(data, {
        ...callbacks,
        onAttachment: (attachment) => {
          if (attachment.senderType === 'ai') {
            files.push({
              filename: attachment.answer || '',
              fileId: attachment.fileId,
              containerId: attachment.containerId,
            });
          } else {
            callbacks?.onAttachment?.(attachment);
          }
        },
      });
      if (chunkResponse) {
        aiRespondingTempt += chunkResponse.aiRespondingTempt;
        errorRespondingTempt += chunkResponse.errorRespondingTempt;
        if (chunkResponse.jsonData.isEnd) {
          if (chunkResponse.jsonData.threadId) {
            setThreadId(chunkResponse.jsonData.threadId);
            setDefaultElementValue(chunkResponse.jsonData.threadId);
          }
          if (chunkResponse.jsonData.responseId) {
            setResponseId(chunkResponse.jsonData.responseId);
            setDefaultElementValue(chunkResponse.jsonData.responseId);
          }
          onDone();
        }
        return;
      }
      onDone();
    });

    es.addEventListener('error', (event) => {
      if (event.type === 'error' || event.type === 'exception') {
        errorRespondingTempt += event.message;
      }
      onDone();
    });

    es.addEventListener('close', (event) => {
      console.log('===Close SSE connection.');
      onDone();
    });
  };

  const getAIResponse = async (callbacks?: CreateChatMessageCallBacks) => {
    try {
      const messages = addMessageQuery({
        content: query,
        role: 'user',
      });
      const body: CreateChatMessageRequest = {
        appId,
        query,
        username,
        conversationId,
        messages,
        threadId,
        responseId,
        webSearchTool,
        initPrompt,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(fileSelected
          ? {
              metadata: {
                fileUrl: fileSelected?.url,
              },
            }
          : {}),
      };
      handleAiResponseMobile(body, callbacks);
      return;
    } catch (error) {
      setAiResponding(false);
      throw error;
    }
  };

  const onSubmit = async () => {
    // ユーザーの質問を表示
    if (query) {
      addMessage({
        senderType: 'user',
        message: query,
        messageType: 'text',
      });
      // 入力フィールドをクリア
      setQuery('');
    }
    setTimeout(() => {
      scrollToBottom();
    }, 300);
    try {
      await getAIResponse({
        onDone: ({ answer, error, files }) => {
          setAiRespondingText(null);
          if (error) {
            addMessage({
              senderType: 'error',
              message: error,
              messageType: 'text',
            });
            return;
          }
          addMessage({
            senderType: 'ai',
            message: answer,
            messageType: 'text',
          });
          addMessageQuery({
            content: answer,
            role: 'assistant',
          });
          // ai file response
          if (files) {
            files.forEach((file) => {
              addMessage({
                senderType: 'ai',
                message: file.filename,
                messageType: 'file',
                attachments: {
                  fileId: file.fileId,
                  containerId: file.containerId,
                },
              });
            });
          }
          setFileSelected(null);
        },
        onResource: ({ url }) => {
          addMessage({
            senderType: 'ai',
            message: url,
            messageType: 'url',
            attachments: { url },
          });
        },
        onAttachment: ({ senderType, answer, fileId, containerId }) => {
          addMessage({
            senderType,
            message: answer || fileId,
            messageType: 'file',
            attachments: { fileId, containerId },
          });
        },
        onAnswer: (answer) => {
          setAiRespondingText((old) => `${old || ''}${answer}`);
          // スクロールを下に
          messageRef?.current?.scrollToEnd({
            animated: false,
          });
        },
      });
    } catch (error: any) {
      console.error('===error', error);
      addMessage({
        senderType: 'error',
        message: error.message || '',
        messageType: 'text',
      });
      // スクロールを下に
      scrollToBottom();
    }
  };

  const handleInit = async () => {
    if (
      initPrompt &&
      gptConfig?.serviceType === GptConversationServiceTypeEnum.openAi
    ) {
      addMessageQuery({
        content: initPrompt,
        role: 'system',
      });
    }
  };

  /**
   * get message history for openAiAssistant if initThreadId is existed
   */
  const getMessageHistories = useCallback(async () => {
    if (!gptConfig.apiKey) {
      console.log('===apiKey is not existed');
      return;
    }
    if (!actions?.getMessageHistory) {
      console.log('===getMessageHistory is not existed');
      return;
    }
    switch (gptConfig.serviceType) {
      case GptConversationServiceTypeEnum.openAiAssistant: {
        if (!gptConfig.assistantId || !initThreadId) {
          break;
        }
        setGetHistoryLoading(true);
        const messageHistory = await actions?.getMessageHistory({
          apiKey: gptConfig.apiKey,
          assistantId: gptConfig.assistantId,
          threadId: initThreadId,
          serviceType: GptConversationServiceTypeEnum.openAiAssistant,
        });
        setGetHistoryLoading(false);
        setMessages((old) => {
          return uniqBy([...old, ...messageHistory], (e) => e.id);
        });
        break;
      }
      case GptConversationServiceTypeEnum.openAiResponses:
        {
          if (!initResponseId) {
            console.log('===initResponseId is not existed');
            break;
          }
          setGetHistoryLoading(true);
          const messageHistory = await actions?.getMessageHistory({
            apiKey: gptConfig.apiKey,
            responseId: initResponseId,
            serviceType: GptConversationServiceTypeEnum.openAiResponses,
          });
          setGetHistoryLoading(false);
          setMessages((old) => {
            return uniqBy([...old, ...messageHistory], (e) => e.id);
          });
        }
        break;
      default:
        break;
    }
    setHistoryInitialized(true);
    scrollToBottom();
  }, [gptConfig.apiKey, gptConfig.serviceType, initThreadId, initResponseId]);

  const downloadFile = async (data: Pick<MessageFile, 'attachments'>) => {
    setAiResponding(true);
    try {
      await actions?.onDownloadFile({
        attachments: data.attachments,
        apiKey: gptConfig.apiKey,
      });
    } catch (error) {
      console.error('===[GptConversation] downloadFile failed', data, error);
    } finally {
      setAiResponding(false);
    }
  };

  const getImageUrlFromMessage = (message: IMessage) => {
    if (message.messageType === 'image') {
      return message.attachments.url;
    }
    if (message.messageType === 'file' && isImageFile(message.message)) {
      const fileUrl = new URL(
        `${API_ENDPOINTS.GPT_CONVERSATION_FILE_CONTENT}/${message.attachments.fileId}`,
        API_BASE_URL
      );
      fileUrl.searchParams.set('apiKey', apiKey);
      fileUrl.searchParams.set('appId', appId);
      if (message.attachments.containerId) {
        fileUrl.searchParams.set(
          'containerId',
          message.attachments.containerId
        );
      }
      return fileUrl.toString();
    }
    return undefined;
  };

  useEffect(() => {
    if (!isCanvas) {
      handleInit().finally(() => {
        setInitializing(false);
      });
    }
  }, []);

  useEffect(() => {
    if (!isCanvas && initThreadId && !historyInitialized) {
      setThreadId(initThreadId);
      setDefaultElementValue(initThreadId);
      getMessageHistories();
    }
  }, [initThreadId, historyInitialized, getMessageHistories]);

  useEffect(() => {
    if (!isCanvas && initResponseId && !historyInitialized) {
      setResponseId(initResponseId);
      setDefaultElementValue(initResponseId);
      getMessageHistories();
    }
  }, [initResponseId, historyInitialized, getMessageHistories]);

  useEffect(() => {
    if (initMessage) {
      addInitMessage(initMessage);
    }
  }, [initMessage]);

  return {
    ...props,
    username,
    aiName,
    submitButtonText,
    placeholderText,
    getSenderName,
    serviceType,
    bindingValue,
    onSubmit,
    setQuery,
    query,
    messages,
    aiRespondingText,
    aiResponding,
    messageRef,
    initializing,
    fileSelected,
    setFileSelected,
    downloadFile,
    getHistoryLoading,
    supportUploadFile,
    isCanvas,
    getImageUrlFromMessage,
  };
};

export type UseGptConversation = ReturnType<typeof useGptConversation>;
